<!-- 人员状态统计 -->
<template>
  <div class="personnel-status-stats">
    <div class="header-section">
      <div class="header-title">
        <h2>人员状态统计</h2>
        <div class="data-update-time">数据更新时间: {{ dataUpdateTime }}</div>
      </div>
      <div class="filter-section">
        <div class="filter-item">
          <span class="filter-label">时间范围:</span>
          <el-select v-model="timeRange" style="width: 120px;" @change="handleTimeRangeChange">
            <el-option label="近7天" value="7" />
            <el-option label="近30天" value="30" />
          </el-select>
        </div>
        <el-button type="primary" @click="handleQuery">查询</el-button>
      </div>
    </div>

    <personnel-stats-cards :stats-data="statsData" :loading="loading" />

    <personnel-charts-section
      :status-list="statusList"
      :team-load="teamLoad"
      :daily-list="dailyList"
      :top-list="topList"
      :loading="chartsLoading"
    />

    <div class="table-section">
      <h3 class="table-title">人员明细（巡检子任务责任人）</h3>
      <el-table v-loading="tableLoading" :data="detailRecords" border stripe style="width: 100%">
        <el-table-column prop="realName" label="姓名" min-width="100" />
        <el-table-column prop="teamName" label="班组" min-width="100" show-overflow-tooltip />
        <el-table-column prop="runningExtCount" label="执行中子任务" width="120" align="center" />
        <el-table-column prop="completedExtCount" label="已完成子任务" width="120" align="center" />
        <el-table-column prop="timeoutExtCount" label="超时子任务" width="100" align="center" />
        <el-table-column prop="pointProgressLabel" label="点位进度" width="110" align="center" />
        <el-table-column prop="currentRouteName" label="当前路线" min-width="140" show-overflow-tooltip />
        <el-table-column prop="statusOverview" label="状态概览" width="100" align="center" />
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next"
        :total="detailTotal"
        :page-size="detailSize"
        :current-page="detailCurrent"
        :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { formatDateTime } from '@/util/date';
import PersonnelStatsCards from './components/PersonnelStatsCards.vue';
import PersonnelChartsSection from './components/PersonnelChartsSection.vue';
import {
  getPersonnelSummary,
  getPersonnelStatusDistribution,
  getPersonnelTeamLoad,
  getPersonnelDaily,
  getPersonnelTopCompleted,
  getPersonnelDetailPage
} from './api';

export default {
  name: 'PersonnelStatusStats',
  components: { PersonnelStatsCards, PersonnelChartsSection },
  data() {
    return {
      loading: false,
      chartsLoading: false,
      tableLoading: false,
      timeRange: '7',
      dataUpdateTime: '',
      statsData: {},
      statusList: [],
      teamLoad: [],
      dailyList: [],
      topList: [],
      detailRecords: [],
      detailTotal: 0,
      detailCurrent: 1,
      detailSize: 10
    };
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      this.dataUpdateTime = formatDateTime(new Date());
      await this.loadSummary();
      await this.loadCharts();
      await this.loadDetail();
    },
    daysNum() {
      return parseInt(this.timeRange, 10) || 7;
    },
    async loadSummary() {
      this.loading = true;
      try {
        const res = await getPersonnelSummary(this.daysNum());
        if (res.data && res.data.data) {
          this.statsData = res.data.data;
        }
      } catch (e) {
        console.error(e);
        this.$message.error('加载汇总失败');
      } finally {
        this.loading = false;
      }
    },
    async loadCharts() {
      this.chartsLoading = true;
      const d = this.daysNum();
      try {
        const [st, tm, dy, tp] = await Promise.all([
          getPersonnelStatusDistribution(d),
          getPersonnelTeamLoad(d),
          getPersonnelDaily(d),
          getPersonnelTopCompleted(d, 10)
        ]);
        this.statusList = (st.data && st.data.data) || [];
        this.teamLoad = (tm.data && tm.data.data) || [];
        this.dailyList = (dy.data && dy.data.data) || [];
        this.topList = (tp.data && tp.data.data) || [];
      } catch (e) {
        console.error(e);
        this.$message.error('加载图表失败');
      } finally {
        this.chartsLoading = false;
      }
    },
    async loadDetail() {
      this.tableLoading = true;
      try {
        const res = await getPersonnelDetailPage(this.daysNum(), this.detailCurrent, this.detailSize);
        const page = res.data && res.data.data;
        if (page) {
          this.detailRecords = page.records || [];
          this.detailTotal = page.total != null ? page.total : 0;
        }
      } catch (e) {
        console.error(e);
        this.$message.error('加载明细失败');
      } finally {
        this.tableLoading = false;
      }
    },
    handleTimeRangeChange() {
      this.detailCurrent = 1;
      this.loadSummary();
      this.loadCharts();
      this.loadDetail();
    },
    handleQuery() {
      this.dataUpdateTime = formatDateTime(new Date());
      this.detailCurrent = 1;
      this.loadSummary();
      this.loadCharts();
      this.loadDetail();
    },
    handlePageChange(p) {
      this.detailCurrent = p;
      this.loadDetail();
    },
    handleSizeChange(s) {
      this.detailSize = s;
      this.detailCurrent = 1;
      this.loadDetail();
    }
  }
};
</script>

<style lang="scss" scoped>
.personnel-status-stats {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  .header-section {
    background: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    .header-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 12px;
      h2 {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        color: #303133;
      }
      .data-update-time { font-size: 14px; color: #909399; }
    }
    .filter-section {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;
      .filter-item {
        display: flex;
        align-items: center;
        gap: 8px;
        .filter-label { font-size: 14px; color: #606266; white-space: nowrap; }
      }
      .filter-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
    }
  }
  .table-section {
    margin-top: 20px;
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    .table-title {
      margin: 0 0 12px;
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
    .pagination { margin-top: 16px; text-align: right; }
  }
}
</style>
